// Country flag emoji helpers.
//
// Flag emoji (regional indicator symbols) are only valid for ISO 3166-1
// alpha-2 codes. A 3-letter alpha-3 code or a full country name produces no
// flag, so callers should run their value through `toFlagCountryCode` before
// converting to an emoji.

// ISO 3166-1 alpha-3 -> alpha-2. Used to derive a valid flag from a 3-letter
// country code (e.g. "VNM" -> "VN", "USA" -> "US", "CHN" -> "CN").
const ISO3_TO_ISO2: Record<string, string> = {
  AFG: 'AF', ALB: 'AL', DZA: 'DZ', ASM: 'AS', AND: 'AD', AGO: 'AO', AIA: 'AI',
  ATA: 'AQ', ATG: 'AG', ARG: 'AR', ARM: 'AM', ABW: 'AW', AUS: 'AU', AUT: 'AT',
  AZE: 'AZ', BHS: 'BS', BHR: 'BH', BGD: 'BD', BRB: 'BB', BLR: 'BY', BEL: 'BE',
  BLZ: 'BZ', BEN: 'BJ', BMU: 'BM', BTN: 'BT', BOL: 'BO', BIH: 'BA', BWA: 'BW',
  BVT: 'BV', BRA: 'BR', IOT: 'IO', VGB: 'VG', BRN: 'BN', BGR: 'BG', BFA: 'BF',
  BDI: 'BI', CPV: 'CV', KHM: 'KH', CMR: 'CM', CAN: 'CA', CYM: 'KY', CAF: 'CF',
  TCD: 'TD', CHL: 'CL', CHN: 'CN', CXR: 'CX', CCK: 'CC', COL: 'CO', COM: 'KM',
  COG: 'CG', COD: 'CD', COK: 'CK', CRI: 'CR', CIV: 'CI', HRV: 'HR', CUB: 'CU',
  CUW: 'CW', CYP: 'CY', CZE: 'CZ', DNK: 'DK', DJI: 'DJ', DMA: 'DM', DOM: 'DO',
  ECU: 'EC', EGY: 'EG', SLV: 'SV', GNQ: 'GQ', ERI: 'ER', EST: 'EE', SWZ: 'SZ',
  ETH: 'ET', FLK: 'FK', FRO: 'FO', FJI: 'FJ', FIN: 'FI', FRA: 'FR', GUF: 'GF',
  PYF: 'PF', ATF: 'TF', GAB: 'GA', GMB: 'GM', GEO: 'GE', DEU: 'DE', GHA: 'GH',
  GIB: 'GI', GRC: 'GR', GRL: 'GL', GRD: 'GD', GLP: 'GP', GUM: 'GU', GTM: 'GT',
  GGY: 'GG', GIN: 'GN', GNB: 'GW', GUY: 'GY', HTI: 'HT', HMD: 'HM', VAT: 'VA',
  HND: 'HN', HKG: 'HK', HUN: 'HU', ISL: 'IS', IND: 'IN', IDN: 'ID', IRN: 'IR',
  IRQ: 'IQ', IRL: 'IE', IMN: 'IM', ISR: 'IL', ITA: 'IT', JAM: 'JM', JPN: 'JP',
  JEY: 'JE', JOR: 'JO', KAZ: 'KZ', KEN: 'KE', KIR: 'KI', PRK: 'KP', KOR: 'KR',
  KWT: 'KW', KGZ: 'KG', LAO: 'LA', LVA: 'LV', LBN: 'LB', LSO: 'LS', LBR: 'LR',
  LBY: 'LY', LIE: 'LI', LTU: 'LT', LUX: 'LU', MAC: 'MO', MDG: 'MG', MWI: 'MW',
  MYS: 'MY', MDV: 'MV', MLI: 'ML', MLT: 'MT', MHL: 'MH', MTQ: 'MQ', MRT: 'MR',
  MUS: 'MU', MYT: 'YT', MEX: 'MX', FSM: 'FM', MDA: 'MD', MCO: 'MC', MNG: 'MN',
  MNE: 'ME', MSR: 'MS', MAR: 'MA', MOZ: 'MZ', MMR: 'MM', NAM: 'NA', NRU: 'NR',
  NPL: 'NP', NLD: 'NL', NCL: 'NC', NZL: 'NZ', NIC: 'NI', NER: 'NE', NGA: 'NG',
  NIU: 'NU', NFK: 'NF', MNP: 'MP', NOR: 'NO', OMN: 'OM', PAK: 'PK', PLW: 'PW',
  PSE: 'PS', PAN: 'PA', PNG: 'PG', PRY: 'PY', PER: 'PE', PHL: 'PH', PCN: 'PN',
  POL: 'PL', PRT: 'PT', PRI: 'PR', QAT: 'QA', REU: 'RE', ROU: 'RO', RUS: 'RU',
  RWA: 'RW', BLM: 'BL', SHN: 'SH', KNA: 'KN', LCA: 'LC', MAF: 'MF', SPM: 'PM',
  VCT: 'VC', WSM: 'WS', SMR: 'SM', STP: 'ST', SAU: 'SA', SEN: 'SN', SRB: 'RS',
  SYC: 'SC', SLE: 'SL', SGP: 'SG', SXM: 'SX', SVK: 'SK', SVN: 'SI', SLB: 'SB',
  SOM: 'SO', ZAF: 'ZA', SGS: 'GS', SSD: 'SS', ESP: 'ES', LKA: 'LK', SDN: 'SD',
  SUR: 'SR', SJM: 'SJ', SWE: 'SE', CHE: 'CH', SYR: 'SY', TWN: 'TW', TJK: 'TJ',
  TZA: 'TZ', THA: 'TH', TLS: 'TL', TGO: 'TG', TKL: 'TK', TON: 'TO', TTO: 'TT',
  TUN: 'TN', TUR: 'TR', TKM: 'TM', TCA: 'TC', TUV: 'TV', UGA: 'UG', UKR: 'UA',
  ARE: 'AE', GBR: 'GB', USA: 'US', UMI: 'UM', URY: 'UY', UZB: 'UZ', VUT: 'VU',
  VEN: 'VE', VNM: 'VN', VIR: 'VI', WLF: 'WF', ESH: 'EH', YEM: 'YE', ZMB: 'ZM',
  ZWE: 'ZW', XKX: 'XK',
};

// Common country names -> alpha-2 code (Indonesian + English).
const COUNTRY_NAME_TO_ISO2: Record<string, string> = {
  INDONESIA: 'ID',
  SINGAPORE: 'SG', SINGAPURA: 'SG',
  'HONG KONG': 'HK', HONGKONG: 'HK',
  CHINA: 'CN', TIONGKOK: 'CN', TAIWAN: 'TW',
  MALAYSIA: 'MY',
  JAPAN: 'JP', JEPANG: 'JP',
  KOREA: 'KR', 'SOUTH KOREA': 'KR', 'KOREA SELATAN': 'KR',
  THAILAND: 'TH',
  VIETNAM: 'VN',
  PHILIPPINES: 'PH', FILIPINA: 'PH',
  INDIA: 'IN',
  'UNITED KINGDOM': 'GB', UK: 'GB', INGGRIS: 'GB', ENGLAND: 'GB',
  GERMANY: 'DE', JERMAN: 'DE',
  FRANCE: 'FR', PERANCIS: 'FR',
  ITALY: 'IT', ITALIA: 'IT',
  SPAIN: 'ES', SPANYOL: 'ES',
  NETHERLANDS: 'NL', BELANDA: 'NL',
  BELGIUM: 'BE', BELGIA: 'BE',
  SWITZERLAND: 'CH', SWISS: 'CH',
  SWEDEN: 'SE', SWEDIA: 'SE',
  'UNITED STATES': 'US', USA: 'US', 'AMERIKA SERIKAT': 'US',
  CANADA: 'CA', KANADA: 'CA',
  BRAZIL: 'BR', BRASIL: 'BR',
  MEXICO: 'MX', MEKSIKO: 'MX',
  ARGENTINA: 'AR',
  AUSTRALIA: 'AU',
  'NEW ZEALAND': 'NZ', 'SELANDIA BARU': 'NZ',
  'UNITED ARAB EMIRATES': 'AE', UAE: 'AE',
  'SAUDI ARABIA': 'SA', 'ARAB SAUDI': 'SA',
  QATAR: 'QA',
  TURKEY: 'TR', TURKI: 'TR',
};

/**
 * Resolves an arbitrary country value into a 2-letter ISO 3166-1 alpha-2 code
 * suitable for building a flag emoji. Accepts an existing alpha-2 code, an
 * alpha-3 code, or a country name. Returns '' when nothing can be resolved, so
 * callers never render a wrong/missing flag.
 */
export function toFlagCountryCode(value: string | undefined | null): string {
  if (!value) return '';
  const code = value.trim().toUpperCase();

  if (/^[A-Z]{2}$/.test(code)) return code;
  if (ISO3_TO_ISO2[code]) return ISO3_TO_ISO2[code];
  if (COUNTRY_NAME_TO_ISO2[code]) return COUNTRY_NAME_TO_ISO2[code];

  // Partial-name match (e.g. "SINGAPORE - SG", "MY - MALAYSIA", "KOREA, REPUBLIC OF")
  for (const [name, iso2] of Object.entries(COUNTRY_NAME_TO_ISO2)) {
    if (code.includes(name) || name.includes(code)) return iso2;
  }

  const codeMatch = code.match(/^([A-Z]{2})\s*[-_]/);
  if (codeMatch) return codeMatch[1];

  return '';
}

/**
 * Converts a 2-letter ISO code into the regional-indicator flag emoji.
 * Returns '' for anything that is not exactly 2 letters.
 */
export function toFlagEmoji(iso2: string): string {
  if (!/^[A-Z]{2}$/.test(iso2)) return '';
  return iso2.replace(/./g, (ch) =>
    String.fromCodePoint(0x1f1e6 + ch.charCodeAt(0) - 0x41),
  );
}

/**
 * Path to the vendored 4:3 flag SVG for a 2-letter ISO code.
 *
 * Flags are served from our own origin (`public/flags/`) rather than drawn as
 * emoji, because Windows ships no regional-indicator flag glyphs: `toFlagEmoji`
 * renders as the boxed letters "CN" there, not a flag. See public/flags/README.md.
 *
 * Returns '' for anything that is not exactly 2 letters, so callers can treat a
 * falsy result as "no flag" rather than pointing an <img> at a 404.
 */
export function flagAssetUrl(iso2: string): string {
  if (!/^[A-Z]{2}$/.test(iso2)) return '';
  return `/flags/${iso2.toLowerCase()}.svg`;
}
